import type { ToolbarDisabled } from '@evisort/eds/components/Toolbar/types';
import type { IconType } from '@evisort/eds/types/SharedIconProps';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface AppShellPanelSlot {
  panelKey: string;
  order: number;
  icon?: IconType;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  count?: number;
  maxCount?: number;
  width?: 'fill';
  actionOnly?: boolean;
  onOpen?: () => void;
  container: HTMLElement;
}

export interface AppShellRailSlot {
  disabled: ToolbarDisabled;
  defaultPanelKey?: string;
  keepPanelsMounted: boolean;
  initialPanelWidth?: number;
  forcedPanelWidth?: number;
  onPanelWidthChange?: (width: number) => void;
}

const DEFAULT_RAIL_SLOT: AppShellRailSlot = {
  disabled: false,
  keepPanelsMounted: false,
};

interface AppShellSlotRegistry {
  header: HTMLElement | null;
  secondaryHeader: HTMLElement | null;
  panels: AppShellPanelSlot[];
  rail: AppShellRailSlot;
  registerHeader: (node: HTMLElement) => () => void;
  registerSecondaryHeader: (node: HTMLElement) => () => void;
  registerPanel: (slot: AppShellPanelSlot) => void;
  unregisterPanel: (panelKey: string) => void;
  registerRail: (slot: AppShellRailSlot) => void;
  unregisterRail: () => void;
}

const AppShellSlotRegistryContext = createContext<AppShellSlotRegistry>({
  header: null,
  secondaryHeader: null,
  panels: [],
  rail: DEFAULT_RAIL_SLOT,
  registerHeader: () => () => undefined,
  registerSecondaryHeader: () => () => undefined,
  registerPanel: () => undefined,
  unregisterPanel: () => undefined,
  registerRail: () => undefined,
  unregisterRail: () => undefined,
});

const isSameDisabled = (a: ToolbarDisabled, b: ToolbarDisabled) => {
  if (a === false || b === false) {
    return a === b;
  }
  return a.disabled === b.disabled && a.disabledReason === b.disabledReason;
};

const isSameRail = (a: AppShellRailSlot, b: AppShellRailSlot) =>
  isSameDisabled(a.disabled, b.disabled) &&
  a.defaultPanelKey === b.defaultPanelKey &&
  a.keepPanelsMounted === b.keepPanelsMounted &&
  a.initialPanelWidth === b.initialPanelWidth &&
  a.forcedPanelWidth === b.forcedPanelWidth &&
  a.onPanelWidthChange === b.onPanelWidthChange;

const isSamePanel = (a: AppShellPanelSlot, b: AppShellPanelSlot) =>
  a.panelKey === b.panelKey &&
  a.order === b.order &&
  a.icon === b.icon &&
  a.label === b.label &&
  a.tooltip === b.tooltip &&
  a.disabled === b.disabled &&
  a.count === b.count &&
  a.maxCount === b.maxCount &&
  a.width === b.width &&
  a.actionOnly === b.actionOnly &&
  a.onOpen === b.onOpen &&
  a.container === b.container;

export function AppShellSlotProvider({ children }: PropsWithChildren) {
  const [header, setHeader] = useState<HTMLElement | null>(null);
  const [secondaryHeader, setSecondaryHeader] = useState<HTMLElement | null>(
    null,
  );
  const [panels, setPanels] = useState<AppShellPanelSlot[]>([]);
  const [rail, setRail] = useState<AppShellRailSlot>(DEFAULT_RAIL_SLOT);

  const registerHeader = useCallback((node: HTMLElement) => {
    setHeader(node);
    return () => setHeader((current) => (current === node ? null : current));
  }, []);

  const registerSecondaryHeader = useCallback((node: HTMLElement) => {
    setSecondaryHeader(node);
    return () =>
      setSecondaryHeader((current) => (current === node ? null : current));
  }, []);

  const registerPanel = useCallback((slot: AppShellPanelSlot) => {
    setPanels((current) => {
      const existing = current.find((p) => p.panelKey === slot.panelKey);
      if (existing && isSamePanel(existing, slot)) {
        return current;
      }
      return [
        ...current.filter((p) => p.panelKey !== slot.panelKey),
        slot,
      ].sort((a, b) => a.order - b.order);
    });
  }, []);

  const unregisterPanel = useCallback((panelKey: string) => {
    setPanels((current) => current.filter((p) => p.panelKey !== panelKey));
  }, []);

  const registerRail = useCallback((slot: AppShellRailSlot) => {
    setRail((current) => (isSameRail(current, slot) ? current : slot));
  }, []);

  const unregisterRail = useCallback(() => setRail(DEFAULT_RAIL_SLOT), []);

  const value = useMemo(
    () => ({
      header,
      secondaryHeader,
      panels,
      rail,
      registerHeader,
      registerSecondaryHeader,
      registerPanel,
      unregisterPanel,
      registerRail,
      unregisterRail,
    }),
    [
      header,
      secondaryHeader,
      panels,
      rail,
      registerHeader,
      registerSecondaryHeader,
      registerPanel,
      unregisterPanel,
      registerRail,
      unregisterRail,
    ],
  );

  return (
    <AppShellSlotRegistryContext.Provider value={value}>
      {children}
    </AppShellSlotRegistryContext.Provider>
  );
}

export function useAppShellSlotRegistry() {
  return useContext(AppShellSlotRegistryContext);
}
